import { LitElement, html, css, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { AuthResponse, AuthFormData, AuthModalConfig, ToastType } from '../../types/auth';
import { apiClient } from '../../utils/api-client';

/**
 * Componente Lit per la gestione dei modali di autenticazione
 * Sostituisce auth-modals.js con type safety e reattività migliorata
 */
@customElement('pm-auth-modal')
export class PmAuthModal extends LitElement {
  @property({ type: Boolean }) isAuthenticated = false;
  @property({ type: String }) nextUrl = '/simulations/';
  @property({ type: Boolean }) autoShowLogin = false;
  @property({ type: Boolean }) autoShowRegister = false;

  @state() private isLoading = false;
  @state() private formErrors: Record<string, string> = {};

  // Bootstrap modal instances
  private loginModal?: any;
  private registerModal?: any;

  static styles = css`
    :host {
      display: contents;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.initializeFromURL();
  }

  firstUpdated() {
    this.initializeBootstrapModals();
    this.setupGlobalFunctions();
    this.handleAutoShow();
  }

  /**
   * Inizializza le configurazioni dall'URL
   */
  private initializeFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    this.autoShowLogin = urlParams.get('show_login') === 'true';
    this.autoShowRegister = urlParams.get('show_register') === 'true';
    this.nextUrl = urlParams.get('next') || '/simulations/';
    this.isAuthenticated = document.body.dataset.userAuthenticated === 'true';
  }

  /**
   * Inizializza i modali Bootstrap
   */
  private initializeBootstrapModals() {
    const loginModalEl = document.getElementById('loginModal');
    const registerModalEl = document.getElementById('registerModal');

    if (loginModalEl && (window as any).bootstrap) {
      this.loginModal = new (window as any).bootstrap.Modal(loginModalEl);
    }

    if (registerModalEl && (window as any).bootstrap) {
      this.registerModal = new (window as any).bootstrap.Modal(registerModalEl);
    }
  }

  /**
   * Configura funzioni globali per compatibilità
   */
  private setupGlobalFunctions() {
    (window as any).openLoginModal = () => this.openLoginModal();
    (window as any).openRegisterModal = () => this.openRegisterModal();
  }

  /**
   * Gestisce l'apertura automatica dei modali
   */
  private handleAutoShow() {
    if (this.isAuthenticated) return;

    if (this.autoShowLogin && this.loginModal) {
      this.loginModal.show();
    } else if (this.autoShowRegister && this.registerModal) {
      this.registerModal.show();
    }
  }

  /**
   * Apre il modale di login
   */
  openLoginModal() {
    if (this.isAuthenticated) {
      window.location.href = this.nextUrl;
      return;
    }

    if (this.loginModal) {
      this.loginModal.show();
    }
  }

  /**
   * Apre il modale di registrazione
   */
  openRegisterModal() {
    if (this.isAuthenticated) {
      window.location.href = this.nextUrl;
      return;
    }

    if (this.registerModal) {
      this.registerModal.show();
    }
  }

  /**
   * Gestisce l'invio del form di autenticazione
   */
  private async handleAuthSubmit(event: Event, action: 'login' | 'register') {
    event.preventDefault();
    
    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);
    formData.append('action', action);
    formData.append('next', this.nextUrl);

    this.isLoading = true;
    this.clearFormErrors();

    const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
    const originalText = submitBtn.innerHTML;
    submitBtn.innerHTML = '<i class="fas fa-spinner fa-spin me-2"></i>Attendere...';
    submitBtn.disabled = true;

    try {
      const data = await apiClient.post<AuthResponse>('/', formData);

      if (data.success) {
        this.showToast(data.message || 'Login effettuato con successo', 'success');
        setTimeout(() => {
          window.location.href = data.redirect_url || this.nextUrl;
        }, 1000);
      } else {
        this.showFormErrors(form, data.errors || {});
      }
    } catch (error) {
      console.error('Auth error:', error);
      this.showToast('Errore di connessione. Riprova.', 'error');
    } finally {
      this.isLoading = false;
      submitBtn.innerHTML = originalText;
      submitBtn.disabled = false;
    }
  }

  /**
   * Mostra errori nel form
   */
  private showFormErrors(form: HTMLFormElement, errors: Record<string, string>) {
    this.formErrors = errors;

    Object.entries(errors).forEach(([field, message]) => {
      const input = form.querySelector(`[name="${field}"]`) as HTMLInputElement;
      if (input) {
        input.classList.add('is-invalid');

        // Rimuovi feedback esistente
        const existingFeedback = input.parentNode?.querySelector('.invalid-feedback');
        if (existingFeedback) {
          existingFeedback.remove();
        }

        // Aggiungi nuovo feedback
        const feedback = document.createElement('div');
        feedback.className = 'invalid-feedback';
        feedback.textContent = message;
        input.parentNode?.appendChild(feedback);
      }
    });

    if (errors.general) {
      this.showToast(errors.general, 'error');
    }
  }

  /**
   * Pulisce errori dal form
   */
  private clearFormErrors() {
    this.formErrors = {};

    // Pulisci classi di errore
    this.shadowRoot?.querySelectorAll('.is-invalid').forEach(input => {
      input.classList.remove('is-invalid');
    });

    // Pulisci feedback
    this.shadowRoot?.querySelectorAll('.invalid-feedback').forEach(feedback => {
      feedback.remove();
    });
  }

  /**
   * Mostra toast usando il sistema globale
   */
  private showToast(message: string, type: ToastType) {
    // Usa il sistema toast globale se disponibile
    const globalShowToast = (window as any).showToast;
    if (globalShowToast) {
      globalShowToast({
        message,
        variant: type === 'error' ? 'danger' : type,
        duration: type === 'success' ? 3000 : 5000
      });
    } else {
      // Fallback con alert Bootstrap
      this.showBootstrapAlert(message, type);
    }
  }

  /**
   * Fallback per mostrare alert Bootstrap
   */
  private showBootstrapAlert(message: string, type: ToastType) {
    const alertType = type === 'error' ? 'danger' : type;
    const alertContainer = document.createElement('div');
    alertContainer.className = `alert alert-${alertType} alert-dismissible fade show position-fixed`;
    alertContainer.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 300px;';
    alertContainer.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `;

    document.body.appendChild(alertContainer);

    // Auto-rimozione dopo 5 secondi
    setTimeout(() => {
      if (alertContainer.parentNode) {
        alertContainer.remove();
      }
    }, 5000);
  }

  render() {
    // Questo componente si appoggia ai modali HTML esistenti
    // Il render è vuoto perché gestisce solo la logica
    return html``;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pm-auth-modal': PmAuthModal;
  }
}